/**
 * Patrón de Ruptura — detección de canales convergentes + breakout scoring.
 *
 * Detecta canales convergentes (cuñas, triángulos, banderines) ajustando
 * líneas de tendencia a pivotes mediante boundary-fit, y espera una ruptura
 * confirmada para generar señales Long/Short con SL/TP medidos.
 */

import { log as debugLog, warn as debugWarn } from '@/utils/logger'

// ── Helpers ────────────────────────────────────────────────────────────────────

// Nueva implementación de detect() y helpers fiel a Pine Script v1.7.2

function calcATR(candles, period) {
  const n = candles.length
  const tr = candles.map((c, i) =>
    i === 0
      ? c.high - c.low
      : Math.max(c.high - c.low, Math.abs(c.high - candles[i - 1].close), Math.abs(c.low - candles[i - 1].close))
  )
  const out = new Array(n).fill(null)
  if (n < period) return out
  let sum = 0
  for (let i = 0; i < period; i++) sum += tr[i]
  out[period - 1] = sum / period
  for (let i = period; i < n; i++) out[i] = (out[i - 1] * (period - 1) + tr[i]) / period
  return out
}

function calcSMA(arr, len) {
  const out = new Array(arr.length).fill(null)
  for (let i = len - 1; i < arr.length; i++) {
    let s = 0, cnt = 0
    for (let j = i - len + 1; j <= i; j++) {
      if (arr[j] != null && !isNaN(arr[j])) { s += arr[j]; cnt++ }
    }
    if (cnt > 0) out[i] = s / cnt
  }
  return out
}

function calcRSI(close, period = 14) {
  const n = close.length
  const rsi = new Array(n).fill(null)
  if (n <= period) return rsi
  const gains = new Array(n).fill(0)
  const losses = new Array(n).fill(0)
  for (let i = 1; i < n; i++) {
    const d = close[i] - close[i - 1]
    gains[i] = d > 0 ? d : 0
    losses[i] = d < 0 ? -d : 0
  }
  let avgGain = 0, avgLoss = 0
  for (let i = 1; i <= period; i++) {
    avgGain += gains[i]
    avgLoss += losses[i]
  }
  avgGain /= period
  avgLoss /= period
  rsi[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss)
  for (let i = period + 1; i < n; i++) {
    avgGain = (avgGain * (period - 1) + gains[i]) / period
    avgLoss = (avgLoss * (period - 1) + losses[i]) / period
    rsi[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss)
  }
  return rsi
}

function lineAt(x1, y1, x2, y2, x) {
  const dx = x2 - x1
  if (Math.abs(dx) < 1e-10) return y1
  return y1 + (y2 - y1) * (x - x1) / dx
}

function safeDiv(num, den, fallback = 0.0) {
  return den !== 0 && !isNaN(num) && !isNaN(den) ? num / den : fallback
}

function emptyResult() {
  return {
    pendingChannels: [],
    allChannels: [],
    signals: [],
    channelMarkers: [],
    activeTrade: null,
    lastSignal: null,
    dashboard: {
      pattern: 'Scanning',
      strength: '—',
      trade: 'No Trade',
      bullProb: 0,
      rr: '—',
      touches: '—',
      volContraction: '—',
      convergence: '—',
      totalPatterns: 0,
      breaks: '0▲ 0▼',
      winRate: '—',
      lastSignal: '—',
    },
    stats: { win: 0, loss: 0, timeout: 0 },
  }
}

function detect(candles, config) {
  const cfg = { ...defaultConfig, ...(config || {}) }
  const n = candles.length
  const pivotLen = cfg.pivotLen
  const minTouches = cfg.minTouches
  const maxChannelBars = cfg.maxChannelBars
  const convergenceMin = cfg.convergenceMin
  const minChannelWidthAtr = cfg.minChannelWidthAtr
  const deviationMax = cfg.deviationMax
  const touchTolerance = cfg.touchTolerance
  const slPadding = cfg.slPadding
  const volConfirm = cfg.volConfirm
  const volContraction = cfg.volContraction
  const momentumFilter = cfg.momentumFilter

  const WARMUP_BARS = Math.max(pivotLen * 2 + maxChannelBars, 50)
  if (n < WARMUP_BARS + 1) {
    if (cfg.debug) debugLog('[breakout_pattern] emptyResult: n=', n, 'need>=', WARMUP_BARS + 1)
    return emptyResult()
  }

  const close = candles.map(c => c.close)
  const open_ = candles.map(c => c.open)
  const high = candles.map(c => c.high)
  const low = candles.map(c => c.low)
  const volume = candles.map(c => c.volume ?? 0)
  const time = candles.map(c => c.time)

  const atr20 = calcATR(candles, 20)
  const rsi = calcRSI(close, 14)
  const volSma = calcSMA(volume, 20)

  const cumVol = []
  let cv = 0
  for (let i = 0; i < n; i++) {
    cv += volume[i]
    cumVol.push(cv)
  }

  const hiPrices = []
  const hiBars = []
  const loPrices = []
  const loBars = []

  let channelActive = false
  let hiX1 = 0, hiY1 = 0, hiX2 = 0, hiY2 = 0
  let loX1 = 0, loY1 = 0, loX2 = 0, loY2 = 0
  let chHiTouches = 0
  let chLoTouches = 0
  let chConvergence = 0.0
  let chMaxWidth = 0.0
  let chDetectBar = 0
  let breakoutDir = 0
  let breakoutBar = 0
  let breakStrength = '—'
  let volContractionRatio = 0.0
  let entryPrice = 0.0
  let slPrice = 0.0
  let slPriceOrig = 0.0
  let tp1Price = 0.0
  let tp2Price = 0.0
  let tp3Price = 0.0
  let tp1Hit = false
  let tp2Hit = false
  let tp3Hit = false
  let slHit = false
  let beExit = false
  let tradeOpen = false
  let lastTradeResult = '—'
  let totalPatterns = 0
  let bullBreaks = 0
  let bearBreaks = 0
  let winCount = 0
  let lossCount = 0
  let timeoutCount = 0
  let lastSignalBar = 0
  let lastSignalType = '—'
  let dirScore = 50.0

  const allChannels = []
  const signals = []
  const channelMarkers = []

  const debugStats = cfg.debug ? {
    fits: 0, channelsFound: 0, breakouts: 0,
  } : null

  for (let i = 0; i < n; i++) {
    const atr = atr20[i] ?? 1e-9
    const rsiVal = rsi[i] ?? 50.0
    const volSmaVal = volSma[i] ?? 0.0
    const hasVol = volume[i] > 0

    // Detectar pivotes como ta.pivothigh/pivotlow de Pine
    const pivHighIdx = i - pivotLen
    if (pivHighIdx >= pivotLen && pivHighIdx + pivotLen < n) {
      let maxH = -Infinity
      for (let j = pivHighIdx - pivotLen; j <= pivHighIdx + pivotLen; j++) {
        maxH = Math.max(maxH, high[j])
      }
      if (high[pivHighIdx] === maxH) {
        hiPrices.unshift(high[pivHighIdx])
        hiBars.unshift(pivHighIdx)
        if (hiPrices.length > 60) { hiPrices.pop(); hiBars.pop() }
      }
    }

    const pivLowIdx = i - pivotLen
    if (pivLowIdx >= pivotLen && pivLowIdx + pivotLen < n) {
      let minL = Infinity
      for (let j = pivLowIdx - pivotLen; j <= pivLowIdx + pivotLen; j++) {
        minL = Math.min(minL, low[j])
      }
      if (low[pivLowIdx] === minL) {
        loPrices.unshift(low[pivLowIdx])
        loBars.unshift(pivLowIdx)
        if (loPrices.length > 60) { loPrices.pop(); loBars.pop() }
      }
    }

    const newHighPivot = hiBars.length > 0 && hiBars[0] === pivHighIdx && pivHighIdx >= 0
    const newLowPivot = loBars.length > 0 && loBars[0] === pivLowIdx && pivLowIdx >= 0
    const rescanDue = i % 10 === 0
    const shouldScan = (newHighPivot || newLowPivot || rescanDue) && !channelActive && !tradeOpen && i >= WARMUP_BARS && hiPrices.length >= minTouches && loPrices.length >= minTouches

    let channelJustDetected = false

    if (shouldScan) {
      if (cfg.debug) debugStats.fits++

      const hX = [], hY = [], lX = [], lY = []
      const maxScan = Math.min(hiPrices.length, 15)
      for (let k = 0; k < maxScan; k++) {
        if (i - hiBars[k] <= maxChannelBars) {
          hX.push(hiBars[k])
          hY.push(hiPrices[k])
        }
      }
      const maxScanLo = Math.min(loPrices.length, 15)
      for (let k = 0; k < maxScanLo; k++) {
        if (i - loBars[k] <= maxChannelBars) {
          lX.push(loBars[k])
          lY.push(loPrices[k])
        }
      }

      const hCnt = hX.length
      const lCnt = lX.length
      const devTol = atr * deviationMax
      const touchTol = atr * touchTolerance

      let bestHiX1 = 0, bestHiY1 = 0, bestHiX2 = 0, bestHiY2 = 0
      let bestHiTouches = 0
      let foundHi = false

      if (hCnt >= minTouches) {
        for (let a = 0; a < hCnt - 1; a++) {
          for (let b = a + 1; b < hCnt; b++) {
            const ax = hX[a], ay = hY[a]
            const bx = hX[b], by = hY[b]
            if (Math.abs(bx - ax) >= 1.0) {
              let allBelow = true
              let touches = 0
              for (let k = 0; k < hCnt; k++) {
                const projY = lineAt(ax, ay, bx, by, hX[k])
                const diff = hY[k] - projY
                if (diff > devTol) { allBelow = false; break }
                if (Math.abs(diff) <= touchTol) touches++
              }
              if (allBelow && touches >= minTouches && touches > bestHiTouches) {
                bestHiTouches = touches
                bestHiX1 = ax; bestHiY1 = ay; bestHiX2 = bx; bestHiY2 = by
                foundHi = true
              }
            }
          }
        }
      }

      let bestLoX1 = 0, bestLoY1 = 0, bestLoX2 = 0, bestLoY2 = 0
      let bestLoTouches = 0
      let foundLo = false

      if (lCnt >= minTouches) {
        for (let a = 0; a < lCnt - 1; a++) {
          for (let b = a + 1; b < lCnt; b++) {
            const ax = lX[a], ay = lY[a]
            const bx = lX[b], by = lY[b]
            if (Math.abs(bx - ax) >= 1.0) {
              let allAbove = true
              let touches = 0
              for (let k = 0; k < lCnt; k++) {
                const projY = lineAt(ax, ay, bx, by, lX[k])
                const diff = lY[k] - projY
                if (diff < -devTol) { allAbove = false; break }
                if (Math.abs(diff) <= touchTol) touches++
              }
              if (allAbove && touches >= minTouches && touches > bestLoTouches) {
                bestLoTouches = touches
                bestLoX1 = ax; bestLoY1 = ay; bestLoX2 = bx; bestLoY2 = by
                foundLo = true
              }
            }
          }
        }
      }

      if (foundHi && foundLo) {
        const hiMinX = Math.min(bestHiX1, bestHiX2)
        const hiMaxX = Math.max(bestHiX1, bestHiX2)
        const loMinX = Math.min(bestLoX1, bestLoX2)
        const loMaxX = Math.max(bestLoX1, bestLoX2)
        const overlapStart = Math.max(hiMinX, loMinX)
        const overlapEnd = Math.min(hiMaxX, loMaxX)
        let maxH = 0.0
        if (overlapEnd > overlapStart) {
          const samples = Math.max(Math.floor((overlapEnd - overlapStart) / 5.0), 2)
          for (let s = 0; s <= samples; s++) {
            const sx = overlapStart + (overlapEnd - overlapStart) * s / samples
            const hu = lineAt(bestHiX1, bestHiY1, bestHiX2, bestHiY2, sx)
            const lo = lineAt(bestLoX1, bestLoY1, bestLoX2, bestLoY2, sx)
            if (hu - lo > maxH) maxH = hu - lo
          }
        } else {
          const midX = (hiMinX + hiMaxX + loMinX + loMaxX) / 4.0
          maxH = Math.abs(lineAt(bestHiX1, bestHiY1, bestHiX2, bestHiY2, midX) - lineAt(bestLoX1, bestLoY1, bestLoX2, bestLoY2, midX))
        }

        const startX = Math.min(Math.min(bestHiX1, bestHiX2), Math.min(bestLoX1, bestLoX2))
        const endX = i
        const upperNow = lineAt(bestHiX1, bestHiY1, bestHiX2, bestHiY2, endX)
        const lowerNow = lineAt(bestLoX1, bestLoY1, bestLoX2, bestLoY2, endX)
        const widthNow = upperNow - lowerNow
        const upperStart = lineAt(bestHiX1, bestHiY1, bestHiX2, bestHiY2, startX)
        const lowerStart = lineAt(bestLoX1, bestLoY1, bestLoX2, bestLoY2, startX)
        const widthStart = upperStart - lowerStart
        const convRate = widthStart > 0 ? 1.0 - safeDiv(widthNow, widthStart, 1.0) : 0.0
        const notInv = widthNow > 0 && widthStart > 0
        const isConv = convRate >= convergenceMin
        const widthOk = widthNow >= atr * minChannelWidthAtr && widthNow < atr * 10.0
        const isInside = notInv && close[i - 1] <= upperNow && close[i - 1] >= lowerNow

        const patStartIdx = Math.floor(startX)
        const patBars = Math.max(i - patStartIdx, 1)
        const safPatBars = Math.min(patBars, i)
        const preBars = Math.min(safPatBars, patStartIdx)

        const volInPat = hasVol && safPatBars > 0
          ? safeDiv(cumVol[i] - (cumVol[i - safPatBars] || 0), safPatBars, volSmaVal)
          : volSmaVal
        const volPrePat = hasVol && preBars > 0
          ? safeDiv((cumVol[i - safPatBars] || 0) - (cumVol[i - safPatBars - preBars] || 0), preBars, volSmaVal)
          : volSmaVal
        const volContRatio = safeDiv(volInPat, volPrePat, 1.0)
        const volContOk = volContraction ? (!hasVol || volContRatio < 0.85) : true

        if (notInv && isConv && widthOk && isInside && volContOk) {
          channelActive = true
          hiX1 = bestHiX1; hiY1 = bestHiY1; hiX2 = bestHiX2; hiY2 = bestHiY2
          loX1 = bestLoX1; loY1 = bestLoY1; loX2 = bestLoX2; loY2 = bestLoY2
          chHiTouches = bestHiTouches
          chLoTouches = bestLoTouches
          chConvergence = convRate
          chMaxWidth = maxH
          chDetectBar = i
          breakoutDir = 0
          breakStrength = '—'
          volContractionRatio = volContRatio
          channelJustDetected = true
          totalPatterns++
          lastTradeResult = '—'

          const upperFn = (x) => lineAt(hiX1, hiY1, hiX2, hiY2, x)
          const lowerFn = (x) => lineAt(loX1, loY1, loX2, loY2, x)
          allChannels.push({
            startIdx: patStartIdx,
            endIdx: i,
            startTime: time[patStartIdx],
            endTime: time[i],
            upperFn,
            lowerFn,
            upperTouches: bestHiTouches,
            lowerTouches: bestLoTouches,
            maxWidth: maxH,
            convergence: convRate,
            volContracted: volContOk && volContraction,
            detectedAt: i,
            brokenDir: 0,
          })
          channelMarkers.push({ time: time[i] })
          if (cfg.debug) {
            debugStats.channelsFound++
            debugLog('[breakout_pattern] channel found at i=', i, 'start=', patStartIdx, 'touches=', bestHiTouches, '/', bestLoTouches, 'conv=', convRate.toFixed(4))
          }
        }
      }
    }

    // Channel timeout (no breakout)
    if (channelActive && breakoutDir === 0 && i - chDetectBar > maxChannelBars) {
      channelActive = false
    }

    // Directional scoring
    if (channelActive && breakoutDir === 0) {
      const upperNow = lineAt(hiX1, hiY1, hiX2, hiY2, i)
      const lowerNow = lineAt(loX1, loY1, loX2, loY2, i)
      const upperPrev = lineAt(hiX1, hiY1, hiX2, hiY2, i - 1)
      const lowerPrev = lineAt(loX1, loY1, loX2, loY2, i - 1)
      const midSlope = ((upperNow + lowerNow) - (upperPrev + lowerPrev)) / 2.0
      const slopeBias = midSlope > 0
        ? Math.min(midSlope / (atr * 0.01), 1.0)
        : Math.max(midSlope / (atr * 0.01), -1.0)
      const rsiBias = (rsiVal - 50.0) / 50.0
      const chRange = upperNow - lowerNow
      const posInCh = chRange > 0 ? (close[i] - lowerNow) / chRange : 0.5
      const posBias = (posInCh - 0.5) * 2.0
      const combined = slopeBias * 0.35 + rsiBias * 0.35 + posBias * 0.30
      dirScore = Math.max(0.0, Math.min(100.0, 50.0 + combined * 50.0))
    }
    if (!channelActive) {
      dirScore = 50.0
    }

    // Breakout detection + strength
    let rawBullBreak = false
    let rawBearBreak = false
    let strengthScore = 0.0
    let breakBoundary = 0.0
    let oppBoundary = 0.0

    const channelMature = channelActive && breakoutDir === 0
    if (channelMature && i >= WARMUP_BARS) {
      const upper = lineAt(hiX1, hiY1, hiX2, hiY2, i)
      const lower = lineAt(loX1, loY1, loX2, loY2, i)
      const bodyLen = Math.abs(close[i] - open_[i])
      const candleLen = high[i] - low[i]
      const bodyRatio = candleLen > 0 ? bodyLen / candleLen : 0.0
      const bodyMid = (open_[i] + close[i]) / 2.0
      const volOk = hasVol ? volume[i] > volSmaVal * volConfirm : true
      const momOk = momentumFilter ? (close[i] > upper ? rsiVal > 50 : rsiVal < 50) : true

      if (close[i] > upper) {
        rawBullBreak = true
        breakBoundary = upper
        oppBoundary = lower
        const penetration = atr > 0 ? Math.min((close[i] - upper) / atr, 2.0) / 2.0 : 0.5
        const bodyCommit = bodyMid > upper ? 1.0 : 0.3
        const volBonus = volOk ? 1.0 : 0.4
        const momBonus = momOk ? 1.0 : 0.4
        strengthScore = (penetration * 0.25 + bodyRatio * 0.15 + bodyCommit * 0.15 + volBonus * 0.25 + momBonus * 0.20) * 100
      }
      if (close[i] < lower) {
        rawBearBreak = true
        breakBoundary = lower
        oppBoundary = upper
        const penetration = atr > 0 ? Math.min((lower - close[i]) / atr, 2.0) / 2.0 : 0.5
        const bodyCommit = bodyMid < lower ? 1.0 : 0.3
        const volBonus = volOk ? 1.0 : 0.4
        const momBonus = momOk ? 1.0 : 0.4
        strengthScore = (penetration * 0.25 + bodyRatio * 0.15 + bodyCommit * 0.15 + volBonus * 0.25 + momBonus * 0.20) * 100
      }
    }

    // Signal processing + target
    if (rawBullBreak) {
      breakoutDir = 1
      breakoutBar = i
      entryPrice = close[i]
      slPrice = oppBoundary - atr * slPadding
      // Salvaguarda: en long SL debe estar por debajo del entry
      if (slPrice >= entryPrice) {
        if (cfg.debug) debugWarn('[breakout_pattern] invalid bull SL (>= entry), forcing lower')
        slPrice = entryPrice - atr * slPadding
      }
      slPriceOrig = slPrice
      const targetPrice = breakBoundary + chMaxWidth
      const fullMove = Math.abs(targetPrice - entryPrice)
      tp1Price = entryPrice + fullMove / 3.0
      tp2Price = entryPrice + fullMove * 2.0 / 3.0
      tp3Price = entryPrice + fullMove
      tp1Hit = false; tp2Hit = false; tp3Hit = false; slHit = false; beExit = false
      tradeOpen = true
      breakStrength = strengthScore >= 65 ? 'Strong' : strengthScore >= 35 ? 'Medium' : 'Weak'
      bullBreaks++
      lastSignalBar = i
      lastSignalType = 'Bull Break'
      signals.push({
        time: time[i], idx: i, dir: 1, entry: entryPrice, sl: slPrice, slOrig: slPriceOrig, tp1: tp1Price, tp2: tp2Price, tp3: tp3Price,
        strength: strengthScore, result: 'active', pnl: null,
      })
      if (allChannels.length > 0) {
        const lastCh = allChannels[allChannels.length - 1]
        lastCh.brokenDir = 1
        lastCh.brokenAt = i
      }
      if (cfg.debug) { debugStats.breakouts++; debugLog('[breakout_pattern] bull breakout at i=', i, 'strength=', strengthScore.toFixed(2)) }
    }

    if (rawBearBreak) {
      breakoutDir = -1
      breakoutBar = i
      entryPrice = close[i]
      slPrice = oppBoundary + atr * slPadding
      // Salvaguarda: en short SL debe estar por encima del entry
      if (slPrice <= entryPrice) {
        if (cfg.debug) debugWarn('[breakout_pattern] invalid bear SL (<= entry), forcing higher')
        slPrice = entryPrice + atr * slPadding
      }
      slPriceOrig = slPrice
      const targetPrice = breakBoundary - chMaxWidth
      const fullMove = Math.abs(entryPrice - targetPrice)
      tp1Price = entryPrice - fullMove / 3.0
      tp2Price = entryPrice - fullMove * 2.0 / 3.0
      tp3Price = entryPrice - fullMove
      tp1Hit = false; tp2Hit = false; tp3Hit = false; slHit = false; beExit = false
      tradeOpen = true
      breakStrength = strengthScore >= 65 ? 'Strong' : strengthScore >= 35 ? 'Medium' : 'Weak'
      bearBreaks++
      lastSignalBar = i
      lastSignalType = 'Bear Break'
      signals.push({
        time: time[i], idx: i, dir: -1, entry: entryPrice, sl: slPrice, slOrig: slPriceOrig, tp1: tp1Price, tp2: tp2Price, tp3: tp3Price,
        strength: strengthScore, result: 'active', pnl: null,
      })
      if (allChannels.length > 0) {
        const lastCh = allChannels[allChannels.length - 1]
        lastCh.brokenDir = -1
        lastCh.brokenAt = i
      }
      if (cfg.debug) { debugStats.breakouts++; debugLog('[breakout_pattern] bear breakout at i=', i, 'strength=', strengthScore.toFixed(2)) }
    }

    // TP / SL / BE hit detection
    if (tradeOpen && breakoutDir !== 0 && !slHit && !beExit && i > breakoutBar) {
      const tpSide = breakoutDir === 1 ? high[i] >= tp1Price : low[i] <= tp1Price
      const tp2Side = breakoutDir === 1 ? high[i] >= tp2Price : low[i] <= tp2Price
      const tp3Side = breakoutDir === 1 ? high[i] >= tp3Price : low[i] <= tp3Price
      const slSide = breakoutDir === 1 ? low[i] <= slPrice : high[i] >= slPrice

      if (slSide) {
        if (tp1Hit) {
          beExit = true
          lastTradeResult = 'BE Exit'
        } else {
          slHit = true
          lossCount++
          lastTradeResult = 'SL Hit'
        }
      } else {
        if (tpSide && !tp1Hit) {
          tp1Hit = true
          winCount++
          slPrice = entryPrice
        }
        if (tp2Side && !tp2Hit) tp2Hit = true
        if (tp3Side && !tp3Hit) {
          tp3Hit = true
          lastTradeResult = 'TP3 ✓'
        }
      }
    }

    // Trade timeout
    const tradeTimedOut = tradeOpen && breakoutDir !== 0 && !(tp3Hit || slHit || beExit) && i - breakoutBar > maxChannelBars * 3
    if (tradeTimedOut) {
      timeoutCount++
      lastTradeResult = tp1Hit ? 'Timeout (TP1 ✓)' : 'Timeout'
      tradeOpen = false
      channelActive = false
      breakoutDir = 0
    }

    // Close trade when TP3, SL, BE or timeout
    if (tradeOpen && (tp3Hit || slHit || beExit || tradeTimedOut)) {
      tradeOpen = false
      channelActive = false
      breakoutDir = 0
    }

    // Update last signal result
    if (signals.length > 0 && !tradeOpen) {
      const sig = signals[signals.length - 1]
      if (sig.result === 'active') {
        if (tp3Hit) { sig.result = 'win'; sig.pnl = breakoutDir === 1 ? tp3Price - entryPrice : entryPrice - tp3Price; sig.closeTime = time[i] }
        else if (slHit) { sig.result = 'loss'; sig.pnl = breakoutDir === 1 ? slPriceOrig - entryPrice : entryPrice - slPriceOrig; sig.closeTime = time[i] }
        else if (beExit) { sig.result = 'win'; sig.pnl = 0; sig.closeTime = time[i] }
        else if (tradeTimedOut) { sig.result = 'timeout'; sig.pnl = 0; sig.closeTime = time[i] }
        if (tp1Hit) sig.tp1Hit = time[i]
        if (tp2Hit) sig.tp2Hit = time[i]
        if (tp3Hit) sig.tp3Hit = time[i]
      }
    }
  }

  const lastSignal = signals.length ? signals[signals.length - 1] : null
  const activeTrade = tradeOpen
    ? {
        dir: breakoutDir, entry: entryPrice, sl: slPrice, slOrig: slPriceOrig, tp1: tp1Price, tp2: tp2Price, tp3: tp3Price,
        tp1Hit, tp2Hit, tp3Hit, time: lastSignalBar ? time[lastSignalBar] : null,
      }
    : null

  const lastChannel = allChannels.length ? allChannels[allChannels.length - 1] : null

  let patternState = 'Scanning'
  if (channelActive) {
    patternState = breakoutDir === 0 ? 'Active' : (breakoutDir === 1 ? 'Bull Break' : 'Bear Break')
  } else if (tradeOpen) {
    patternState = breakoutDir === 1 ? 'Bull Trade' : 'Bear Trade'
  } else if (lastSignal) {
    patternState = lastSignal.dir === 1 ? 'Bull Break' : 'Bear Break'
  }

  let tradeLabel = 'No Trade'
  if (tradeOpen) {
    tradeLabel = tp2Hit ? 'TP2 ✓' : tp1Hit ? 'TP1 ✓ (BE)' : 'Active'
  } else if (lastTradeResult !== '—') {
    tradeLabel = lastTradeResult
  } else if (lastSignal) {
    tradeLabel = 'Closed'
  }

  let bullProb = 0
  let touchesStr = '—'
  let volContractionStr = '—'
  let convergenceStr = '—'
  if (lastChannel) {
    bullProb = dirScore
    touchesStr = `${lastChannel.upperTouches}H / ${lastChannel.lowerTouches}L`
    if (lastChannel.volContracted) volContractionStr = `<${(volContractionRatio * 100).toFixed(1)}%`
    convergenceStr = `${(lastChannel.convergence * 100).toFixed(1)}%`
  }

  let rrStr = '—'
  if (tradeOpen && slPriceOrig !== 0) {
    const rr = Math.abs(tp1Price - entryPrice) / Math.abs(slPriceOrig - entryPrice)
    rrStr = `1:${rr.toFixed(2)}`
  }

  const totalClosed = winCount + lossCount
  const winRateStr = totalClosed > 0 ? `${(winCount / totalClosed * 100).toFixed(1)}%` : '—'

  let lastSignalStr = '—'
  if (lastSignal) {
    const barsAgo = n - 1 - (lastSignal.idx ?? n - 1)
    const resLabel = lastSignal.result === 'active' ? '' : ` [${lastSignal.result}]`
    lastSignalStr = `${lastSignalType} (${barsAgo} bars)${resLabel}`
  }

  const dashboard = {
    pattern: patternState,
    strength: breakStrength,
    trade: tradeLabel,
    bullProb: Math.round(bullProb * 10) / 10,
    rr: rrStr,
    touches: touchesStr,
    volContraction: volContractionStr,
    convergence: convergenceStr,
    totalPatterns,
    breaks: `${bullBreaks}▲ ${bearBreaks}▼`,
    winRate: winRateStr,
    lastSignal: lastSignalStr,
  }

  if (cfg.debug) {
    debugLog('[breakout_pattern] detect summary:', {
      n, WARMUP_BARS, totalPatterns, signals: signals.length, channelActive, tradeOpen, debugStats,
    })
  }

  return {
    pendingChannels: allChannels.slice(-1),
    allChannels,
    signals,
    channelMarkers,
    activeTrade,
    lastSignal,
    dashboard,
    stats: { win: winCount, loss: lossCount, timeout: timeoutCount },
  }
}


// ── Default config ─────────────────────────────────────────────────────────────

const defaultConfig = {
  _version: '1.3',
  debug: false,
  pivotLen: 5,
  minTouches: 2,
  maxChannelBars: 120,
  convergenceMin: 0.02,
  volConfirm: 1.2,
  volContraction: false,
  momentumFilter: true,
  slPadding: 0.5,
  labelOffset: 20,
  touchTolerance: 0.15,
  deviationMax: 0.3,
  minChannelWidthAtr: 0.5,
  showPatterns: true,
  showSignals: true,
  showBg: true,
  bgOpacity: 0.12,
  historyMode: 'last_only',
  lineColor: '#FBC02D',
  lineWidth: 2,
  channelLineStyle: 0,
  channelLineWidth: 2,
  levelLineStyle: 2,
  levelLineWidth: 1,
  showLevels: true,
  showLevelLabels: true,
  // Niveles individuales
  slColor: '#FF5252',
  slLineWidth: 1,
  slLineStyle: 0,
  entryColor: '#2962FF',
  entryLineWidth: 2,
  entryLineStyle: 0,
  tp1Color: '#00E676',
  tp1LineWidth: 1,
  tp1LineStyle: 2,
  tp2Color: '#00E676',
  tp2LineWidth: 1,
  tp2LineStyle: 2,
  tp3Color: '#00E676',
  tp3LineWidth: 1,
  tp3LineStyle: 0,
  bullColor: '#00E676',
  bearColor: '#FF5252',
}

// Motor extraído byte-idéntico del indicador legacy breakout_pattern (retirado
// del catálogo al cerrar la migración a PRQ, 2026-08-06 — ver docs/indicators/
// patron_ruptura_quantum.md). PRQ es su única presentación; la lógica de
// detección NO se modifica aquí.
export { detect, defaultConfig }
